'use client'

import { useId, useState } from 'react'

import { Box, type BoxProps, Button, Divider, Heading, Text, TextField } from '@doist/marketist'

import cn from 'clsx'
import { AudienceDetails } from 'services/newsletter/constants'

import css from './rich-text-subscribe.module.css'

export type RichTextSubscribeProps = BoxProps & {
    inputId?: string
    heading?: React.ReactNode
    subtitle?: React.ReactNode
    placeholder: string
    subscribe: string
    ariaLabel: string
    caption?: React.ReactNode
    successTitle: string
    successSubtitle: string
    mailchimpTags: string
    showDividers?: boolean
}

/** The subscribe form contains two states: the form input and the success state. */
export function RichTextSubscribe({
    inputId,
    heading,
    subtitle,
    placeholder,
    subscribe,
    ariaLabel,
    caption,
    successTitle,
    successSubtitle,
    mailchimpTags,
    showDividers,
    ...rest
}: RichTextSubscribeProps) {
    const defaultId = useId()
    const id = inputId ?? defaultId
    const [email, setEmail] = useState('')
    const [isSuccess, setSuccess] = useState(false)
    return (
        <Box {...rest}>
            {showDividers ? <Divider semantic={false} /> : null}
            <Box className={cn(css.hug, 'pb-48')}>
                {heading ? (
                    <Heading level="2" size="major-base">
                        {heading}
                    </Heading>
                ) : null}
                {subtitle ? (
                    <Text
                        size="body-base"
                        weight="medium"
                        className={cn(heading ? css.colorSecondary : undefined, 'mbs-16', 'mbe-24')}
                        tag="div"
                    >
                        {subtitle}
                    </Text>
                ) : null}
                <form
                    action={`${AudienceDetails.server}/subscribe/post`}
                    method="POST"
                    target="_blank"
                    className={css.form}
                    onSubmit={() => {
                        setSuccess(true)
                        return true
                    }}
                >
                    <input type="hidden" name="u" value={AudienceDetails.user} />
                    <input type="hidden" name="id" value={AudienceDetails.listId} />
                    <input type="hidden" name="gdpr[91090]" value="Y" />
                    <input type="hidden" name="tags" value={mailchimpTags} />
                    <TextField
                        id={id}
                        name="MERGE0"
                        variant="outline"
                        type="email"
                        value={email}
                        aria-label={ariaLabel}
                        placeholder={placeholder}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        required
                    />
                    <Button type="submit">{subscribe}</Button>
                    {caption && (
                        <Text tag="div" size="body-2xs" className={cn(css.colorSecondary, 'mbs-8')}>
                            {caption}
                        </Text>
                    )}
                </form>
                {isSuccess ? (
                    <Box className={css.successState}>
                        <Heading level="2" size="major-base" className="pbe-16">
                            {successTitle}
                        </Heading>
                        <Text>{successSubtitle}</Text>
                    </Box>
                ) : null}
            </Box>
            {showDividers ? <Divider semantic={false} /> : null}
        </Box>
    )
}
